/*!
 * Cart Group Guard – Device + Subscription
 * Keeps paired device and subscription lines in sync within each group.
 * Groups are identified by the `_group_id` property.
 * Types: `_type: 'device'` and `_type: 'subscription'`.
 */
(function () {
  'use strict';

  if (window.__CART_GROUP_GUARD__) return;
  window.__CART_GROUP_GUARD__ = true;

  var GROUP_ID = '_group_id';
  var TYPE = '_type';
  var DEVICE_TYPE = 'device';
  var SUB_TYPE = 'subscription';
  // Ownership-mode equivalents: the purchased device + its recurring filter
  // subscription. They play the same "device" / "subscription" roles so the
  // group stays locked together exactly like the subscription bundle.
  var DEVICE_TYPES = ['device', 'ownership_device'];
  var SUB_TYPES = ['subscription', 'filter_subscription'];

  var isOnCartPage = window.location.pathname.indexOf('/cart') !== -1;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function url(path) {
    return root + path.replace(/^\/+/, '');
  }

  function getCart() {
    return fetch(url('cart.js'), {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function changeLine(identifier, quantity, byIndex) {
    // cart/change.js accepts either a line key (`id`) or a 1-based line number
    // (`line`). Using `line` is the most reliable fallback when the key cannot
    // be resolved, and it is exactly what the theme itself uses.
    var payload = byIndex
      ? { line: identifier, quantity: quantity }
      : { id: identifier, quantity: quantity };
    return fetch(url('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
        return data;
      });
    });
  }

  // ---------- Helpers ----------
  function getGroupId(item) {
    return item && item.properties && item.properties[GROUP_ID] || null;
  }

  function getType(item) {
    return item && item.properties && item.properties[TYPE] || null;
  }

  function isDevice(item) {
    return DEVICE_TYPES.indexOf(getType(item)) !== -1;
  }

  function isSubscription(item) {
    return SUB_TYPES.indexOf(getType(item)) !== -1;
  }

  function isGrouped(item) {
    return !!getGroupId(item);
  }

  // ---------- State ----------
  var groups = {};
  var groupKeys = new Set();
  var variantIdToKey = {};
  var indexToKey = {};
  var stateLoaded = false;

  function refreshFromCart(cart) {
    groups = {};
    groupKeys = new Set();
    variantIdToKey = {};
    indexToKey = {};

    (cart.items || []).forEach(function (item, index) {
      variantIdToKey[item.id] = item.key;
      if (item.variant_id) variantIdToKey[item.variant_id] = item.key;
      // The rendered cart markup exposes a 1-based `data-index`. Use the array
      // position (index + 1) so the map is always correct, even when cart.js
      // omits the `index` field. This mirrors how the theme itself addresses a
      // line (`parsedState.items[line - 1]`).
      indexToKey[index + 1] = item.key;

      var gid = getGroupId(item);
      if (!gid) return;

      if (!groups[gid]) {
        groups[gid] = {
          deviceKey: null,
          subscriptionKey: null,
          deviceQty: 0,
          subscriptionQty: 0,
          keys: new Set()
        };
      }

      var group = groups[gid];
      group.keys.add(item.key);
      groupKeys.add(item.key);

      if (isDevice(item)) {
        group.deviceKey = item.key;
        group.deviceQty = item.quantity;
      } else if (isSubscription(item)) {
        group.subscriptionKey = item.key;
        group.subscriptionQty = item.quantity;
      }
    });
    stateLoaded = true;
  }

  function refreshFromServer() {
    return getCart().then(refreshFromCart).catch(function () {});
  }

  // ---------- Loader ----------
  var loader = null;

  function ensureStyles() {
    if (document.getElementById('cart-group-loader-style')) return;
    var style = document.createElement('style');
    style.id = 'cart-group-loader-style';
    style.textContent =
      '.cart-group-loader{position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;' +
      'background:rgba(255,255,255,.55);display:flex;align-items:center;justify-content:center;' +
      'flex-direction:column;gap:12px;font-family:inherit;font-size:15px;color:#333}' +
      '.cart-group-loader__spinner{width:34px;height:34px;border:3px solid rgba(0,0,0,.15);' +
      'border-top-color:#000;border-radius:50%;animation:cartGroupSpin .7s linear infinite}' +
      '@keyframes cartGroupSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  function showLoader() {
    if (loader) return;
    ensureStyles();
    loader = document.createElement('div');
    loader.className = 'cart-group-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = '<span class="cart-group-loader__spinner"></span><span>Updating your cart…</span>';
    document.body.appendChild(loader);
  }

  function hideLoader() {
    if (loader) {
      if (loader.parentNode) loader.parentNode.removeChild(loader);
      loader = null;
    }
  }

  // ---------- Apply a target quantity to both lines of a group ----------
  function applyGroupQuantity(groupId, qty) {
    qty = Math.max(0, parseInt(qty, 10) || 0);

    function run() {
      var group = groups[groupId];
      if (!group) return Promise.resolve(null);

      var deviceKey = group.deviceKey;
      var subKey = group.subscriptionKey;
      var changes = [];

      if (deviceKey && group.deviceQty !== qty) changes.push(changeLine(deviceKey, qty));
      if (subKey && group.subscriptionQty !== qty) changes.push(changeLine(subKey, qty));

      if (!changes.length) return Promise.resolve(null);

      return Promise.all(changes)
        .then(function () { return getCart(); })
        .then(function (newCart) {
          refreshFromCart(newCart);
          if (isOnCartPage) {
            hideLoader(); // hide just before the page refresh
            window.location.reload();
            return newCart;
          }
          hideLoader();
          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: newCart } }));
          } catch (e) {}
          return newCart;
        })
        .catch(function (err) {
          hideLoader();
          console.warn('[Cart Group] Change failed:', err);
          return null;
        });
    }

    if (groups[groupId] && (groups[groupId].deviceKey || groups[groupId].subscriptionKey)) {
      return run();
    }

    return getCart().then(function (cart) {
      refreshFromCart(cart);
      return run();
    }).catch(function (err) {
      console.warn('[Cart Group] Could not read cart:', err);
      return null;
    });
  }

  // ---------- Apply a target quantity to a single (non-grouped) line ----------
  // Used as a fallback when the clicked control is NOT part of a group, so the
  // theme is still fully overridden without breaking standalone cart items.
  function applySingleQuantity(identifier, qty, byIndex) {
    qty = Math.max(0, parseInt(qty, 10) || 0);

    return changeLine(identifier, qty, byIndex)
      .then(function (newCart) {
        refreshFromCart(newCart);
        if (isOnCartPage) {
          hideLoader(); // hide just before the page refresh
          window.location.reload();
          return newCart;
        }
        hideLoader();
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: newCart } }));
        } catch (e) {}
        return newCart;
      })
      .catch(function (err) {
        hideLoader();
        console.warn('[Cart Group] Single change failed:', err);
        return null;
      });
  }

  // ---------- Identify rows belonging to any group ----------
  // The theme renders a 1-based `data-index` on the quantity input and on the
  // remove button (the row itself carries no identifier), so resolve the line
  // by that 1-based position.
  function lineIndexForRow(row, element) {
    var el = element || row;
    var idx = el && el.getAttribute ? el.getAttribute('data-index') : null;
    if (!idx && row && row.querySelector) {
      var indexEl = row.querySelector('cart-remove-button[data-index], [data-index]');
      if (indexEl) idx = indexEl.getAttribute('data-index');
    }
    var n = parseInt(idx, 10);
    return isNaN(n) ? null : n;
  }

  // Resolve a cart line key from a row / clicked element using the attributes
  // the theme actually renders:
  //   - `data-key` / `data-line-key`            (when present on the row)
  //   - variant id (`data-variant-id` / `data-quantity-variant-id`)
  //   - 1-based `data-index` position -> indexToKey map
  function keyForRow(row, element) {
    var el = element || row;

    var key = el.getAttribute('data-key') || el.getAttribute('data-line-key');
    if (key) return key;

    var vid = el.getAttribute('data-variant-id') || el.getAttribute('data-quantity-variant-id');
    if (vid && variantIdToKey[vid]) return variantIdToKey[vid];

    var line = lineIndexForRow(row, element);
    if (line && indexToKey[line]) return indexToKey[line];

    return null;
  }

  function isGroupedRow(row, element) {
    var key = keyForRow(row, element);
    return !!key && groupKeys.has(key);
  }

  function rowSelector() {
    return '.cart-item, .cart__item, [data-cart-item]';
  }

  function getGroupIdFromRow(row, element) {
    var key = keyForRow(row, element);
    if (!key) return null;
    for (var gid in groups) {
      if (groups[gid].keys.has(key)) return gid;
    }
    return null;
  }

  function getGroupIdForKey(key) {
    if (!key) return null;
    for (var gid in groups) {
      if (groups[gid].keys.has(key)) return gid;
    }
    return null;
  }

  // ---------- Intercept quantity and remove clicks on cart rows ----------
  // A capture-phase handler on `window` runs before the theme's handlers, so
  // for ANY cart + / - / remove button we fully override the theme. Grouped
  // rows keep both lines in sync; standalone rows are still updated normally.
  var syncing = false;

  var QTY_BTN_SELECTOR =
    '.quantity__button[name="minus"], .quantity__button[name="plus"], ' +
    '.quantity__button[data-quantity-minus], .quantity__button[data-quantity-plus]';

  var PLUS_BTN_SELECTOR =
    '.quantity__button[name="plus"], .quantity__button[data-quantity-plus]';

  var REMOVE_SELECTOR =
    'cart-remove-button, a[href*="/cart/change"], button[name="remove"], ' +
    '[data-cart-remove], [data-cart-item-remove]';

  // Decide whether a line belongs to a group and apply the right update.
  // `key` is the preferred identifier; `line` is the 1-based index used as a
  // reliable fallback (and to re-resolve the key if state wasn't loaded yet).
  // If the cart state hasn't loaded, fetch it once so grouping is accurate.
  function resolveAndApply(key, line, qty) {
    function apply() {
      var resolvedKey = key || (line && indexToKey[line]) || null;
      var gid = resolvedKey ? getGroupIdForKey(resolvedKey) : null;
      if (gid) return applyGroupQuantity(gid, qty);
      if (resolvedKey) return applySingleQuantity(resolvedKey, qty, false);
      if (line) return applySingleQuantity(line, qty, true);
      return Promise.resolve(null);
    }
    if (stateLoaded) return apply();
    return getCart().then(function (cart) {
      refreshFromCart(cart);
      return apply();
    }).catch(function () {
      stateLoaded = true;
      return apply();
    });
  }

  window.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var row = target.closest(rowSelector());
    if (!row) return;

    var isRemove = !!target.closest(REMOVE_SELECTOR);
    var isQtyBtn = !!target.closest(QTY_BTN_SELECTOR);
    if (!isRemove && !isQtyBtn) return;

    // We own this control now. Because this capture-phase handler on `window`
    // fires before the theme's element/target handlers, blocking propagation
    // here guarantees ONLY our sync logic runs for +, - and remove.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Show our loader IMMEDIATELY (synchronously) on the click, before any
    // network request or cart read, so the user never sees the theme's UI.
    showLoader();

    var line = lineIndexForRow(row, target);
    var key = keyForRow(row, target);
    if (!key && !line) {
      hideLoader();
      return;
    }

    var qty;
    if (isRemove) {
      qty = 0; // delete one -> delete the whole group (or just this line)
    } else {
      var input = row.querySelector('.quantity__input');
      var current = parseInt(input && input.value, 10);
      current = isNaN(current) ? 0 : current;
      var isPlus = !!target.closest(PLUS_BTN_SELECTOR);
      qty = isPlus ? current + 1 : Math.max(0, current - 1);
      if (input) input.value = qty; // immediate visual feedback
    }

    if (syncing) return; // an update is already in flight; keep the loader on

    syncing = true;
    resolveAndApply(key, line, qty).finally(function () {
      syncing = false;
      hideLoader();
    });
  }, true);

  // ---------- Intercept typed quantity changes (capturing) ----------
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.classList || !target.classList.contains('quantity__input')) return;
    var row = target.closest(rowSelector());
    if (!row) return;

    var qty = parseInt(target.value, 10);
    if (isNaN(qty)) return;

    // Block the theme synchronously, then resolve group vs standalone.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Show our loader immediately on the change, before any async work.
    showLoader();

    var line = lineIndexForRow(row, target);
    var key = keyForRow(row, target);
    if (!key && !line) {
      hideLoader();
      return;
    }

    if (syncing) return; // an update is already in flight; keep the loader on

    syncing = true;
    resolveAndApply(key, line, qty).finally(function () {
      syncing = false;
      hideLoader();
    });
  }, true);

  // ---------- Keep state up to date ----------
  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (cart) refreshFromCart(cart);
    else setTimeout(refreshFromServer, 50);
  });

  document.addEventListener('cart:refresh', function () {
    setTimeout(refreshFromServer, 50);
  });

  // ---------- Init ----------
  refreshFromServer();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshFromServer);
  }
})();